import { authenticationStore } from "@/state/auth/AuthStore";
import { requestSearchContactsForOffice } from "@/state/contact/ContactsEffects";
import { requestSearchMembersForOffice } from "@/state/offices/OfficesEffects";
import { setCurrentTransactionField } from "@/state/transactions/CurrentTransactionEvents";
import { ActiveUser, AuthenticationContext } from "@/type/core/auth/auth.types";
import { Contact } from "@/type/core/dashboard/dashboard.types";
import { Member, Office } from "@/type/core/offices/offices.types";
import { ExtraRecipient } from "@/type/core/transaction/transaction.types";
import { useUnit } from "effector-react";
import { useEffect, useState } from "react";
import { toastError } from "@utils/helpers/toast-helper";
import { useTranslation } from "react-i18next";
import { useCurrentTransaction } from "@/state/transactions/CurrentTransactionHook";

interface UseRecipientReturn {
  members: Member[];
  contacts: Contact[];
  office?: Office;
  extraRecipients: ExtraRecipient[];
  setExtraRecipients: (extraRecipient: ExtraRecipient[]) => void;
  handleAddMe: (ativeUser?: ActiveUser) => void;
}

const useRecipient = (officeId?: string): UseRecipientReturn => {
  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const { t } = useTranslation();
  const office = authStore?.user?.officesForCgu.find(
    (office) => office.id === officeId,
  );

  const currentTransaction = useCurrentTransaction();
  const currentTransactionExtraRecipients =
    currentTransaction?.transactionDetail?.extraRecipients;

  const [members, setMembers] = useState<Member[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [extraRecipients, setExtraRecipients] = useState<ExtraRecipient[]>([]);

  useEffect(() => {
    if (officeId) {
      void requestSearchMembersForOffice({ id: officeId });
      void requestSearchContactsForOffice({ id: officeId });
    }
  }, [officeId]);

  useEffect(() => {
    return requestSearchMembersForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setMembers(result.data.contacts || []);
      } else {
        toastError(t("error.retrieveMembers"));
      }
    });
  });

  useEffect(() => {
    return requestSearchContactsForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setContacts(result.data.contacts || []);
      } else {
        toastError(t("error.toast.contactLoading"));
      }
    });
  });

  useEffect(() => {
    setCurrentTransactionField([
      {
        field: "extraRecipients",
        value: extraRecipients,
      },
    ]);
  }, [extraRecipients]);

  useEffect(() => {
    if (currentTransactionExtraRecipients) {
      setExtraRecipients(currentTransactionExtraRecipients);
    }
  }, [currentTransactionExtraRecipients]);

  const handleAddMe = (user?: ActiveUser) => {
    if (!user) {
      return;
    }
    const extraRecipientsNoDoublon = [...extraRecipients].filter(
      (recipient) => recipient.email !== user.email,
    );
    extraRecipientsNoDoublon.push({
      key: user.email + Date.now().toString(),
      email: user.email,
      firstname: user.firstname,
      lastname: user.lastname,
    });
    setExtraRecipients(extraRecipientsNoDoublon);
  };

  return {
    members,
    contacts,
    office,
    extraRecipients,
    setExtraRecipients,
    handleAddMe,
  };
};

export default useRecipient;
